import { useEffect } from 'react'
import { countryLanguageMap } from '@constants/i18n'
import { useGetCountryLanguage } from '@hooks/useGetCountryLanguage'

// There are thirteen banner languages set up in OneTrust, two per every country
// (aka legal entity: Germany, Italy, Spain, France, Austria, Switzerland)
// local language and English. And also default 'en' for all other EU countries.
// Legal entity defines the variant of legal docs that will be shown on the banner
// as they differ from country to country.

const getOneTrustLanguage = (
  selectedCountry: string | undefined = '',
  selectedLanguage: string | undefined = ''
): string => {
  const country = selectedCountry?.toLowerCase()
  const language = selectedLanguage?.toLowerCase()

  // It should return `en` when there were no country provided,
  // event if the selected language is different from `en`,
  // because languages are coupled with legal docs versions,
  // means if language is `es` the legal docs will be Spanish docs,
  // which is not correct in case there is no country selected.
  // In case of no country legal docs should be EU legal docs,
  // which corresponds to `en` OneTrust language.
  if (!country || !language) return 'en'

  if (!countryLanguageMap[country]) return 'en'

  if (countryLanguageMap[country][language]) {
    return countryLanguageMap[country][language]
  }

  // in case there is no such country/language pair,
  // return English wording for the selected country
  return `en-${country}`
}

type OneTrustLanguageManagerProps = {
  children: React.ReactNode
}

const OneTrustLanguageManager = ({
  children,
}: OneTrustLanguageManagerProps) => {
  const { country, language } = useGetCountryLanguage()
  useEffect(() => {
    const oneTrustLanguage = getOneTrustLanguage(country, language)

    window?.OneTrust?.changeLanguage(oneTrustLanguage)
  }, [country, language])

  return <>{children}</>
}

export { getOneTrustLanguage, OneTrustLanguageManager }
